import { isAutofattura, isNonSalesFiscalDocument, } from './document-flow-type.js';
/**
 * Tolleranza (in euro) usata in tutti i confronti sulle quote commessa:
 * validazione Σ quote ≤ base, clamp del residuo, riscalo automatico.
 * Unica source of truth per frontend e backend: se cambiasse solo da un
 * lato, la UI accetterebbe riparti che l'API rifiuta (o viceversa).
 */
export const COMMESSA_ALLOCATION_EPSILON = 0.01;
/**
 * Status dei documenti che concorrono alla valorizzazione economica di una
 * commessa. Whitelist esplicita: esclude bozze, annullati e gli stati di
 * ciclo preventivo/ordine/DDT.
 */
export const COMMESSA_ACCOUNTABLE_STATUSES = [
    'SENT',
    'PARTIALLY_PAID',
    'PAID',
    'LOCKED',
];
/**
 * Base di allocazione commessa di un documento: l'IMPONIBILE (IVA esclusa),
 * l'unità canonica delle quote (`CommessaDocumentAllocation.amount`).
 * Se il documento non ha `taxableAmount` (fatture senza righe, import
 * parziali, stipendi) la base si deriva dal totale meno l'IVA, così i
 * documenti senza imponibile esplicito restano allocabili.
 */
export function getCommessaAllocationBase(doc) {
    return doc.taxableAmount ?? doc.totalAmount - (doc.taxAmount ?? 0);
}
/**
 * Fatture di acquisto estero TD17/18/19: il documento originale è una
 * sorgente PDF, non una seconda riga economica. L'autofattura allocata
 * rappresenta il costo imponibile anche se trasmessa come OUTGOING.
 * Integrazioni senza flusso economico e documenti commerciali restano esclusi.
 */
export function isCommessaEconomicDocument(doc) {
    if (doc.type === 'PAYROLL')
        return true;
    return (doc.type === 'INVOICE' &&
        (isAutofattura(doc.electronicType) ||
            !isNonSalesFiscalDocument(doc.electronicType)));
}
/** Criterio condiviso con i totali backend e il badge del pannello documenti. */
export function isCountedInCommessaSummary(doc) {
    const accountable = COMMESSA_ACCOUNTABLE_STATUSES.includes(doc.status);
    return accountable && isCommessaEconomicDocument(doc);
}
/**
 * Arrotondamento monetario a 2 decimali. `Number.EPSILON` compensa i casi
 * limite della rappresentazione binaria (es. 1.005 → 1.01, non 1.0).
 */
export function roundToTwoDecimals(value) {
    return Math.round((value + Number.EPSILON) * 100) / 100;
}
/**
 * Tipi FatturaPA che sono per costruzione una fattura di acconto. Un TD01
 * può esserlo solo con la conferma esplicita dell'utente, che vive
 * sull'applicazione (`ordinaryAdvanceConfirmed`) e non sul documento: qui
 * si classifica solo ciò che il tipo documento dichiara da sé.
 */
export const ADVANCE_ELECTRONIC_TYPES = ['TD02', 'TD03'];
export function isAdvanceElectronicType(electronicType) {
    return ADVANCE_ELECTRONIC_TYPES.includes(electronicType ?? '');
}
const NON_DOCUMENT_BUCKETS = [
    'PERSONNEL_COST',
    'WORK_REPORT_LABOR',
    'PERSONNEL_AGGREGATE',
];
/** Fonte senza documenti: la riga del breakdown ha `documentsCount` 0. */
export function isNonDocumentBucket(bucket) {
    return NON_DOCUMENT_BUCKETS.includes(bucket);
}
export function getCommessaSourceBucket(doc) {
    if (doc.type === 'PAYROLL')
        return 'PAYROLL';
    if (isAutofattura(doc.electronicType) || doc.direction !== 'OUTGOING') {
        return 'PURCHASE_INVOICE';
    }
    return isAdvanceElectronicType(doc.electronicType)
        ? 'ADVANCE_INVOICE'
        : 'SALES_INVOICE';
}
/**
 * Classificazione ESAUSTIVA dei bucket. È un `Record` sul tipo unione, non
 * due liste: aggiungere un bucket senza classificarlo qui NON compila, e la
 * stessa mappa serve backend (`buildCommessaSummary`) e frontend (colonne
 * Venduto/Costi del footer commessa) — due enumerazioni separate potevano
 * divergere e contare gli acconti emessi fra i costi, dimezzando il margine.
 */
export const COMMESSA_BUCKET_KIND = {
    SALES_INVOICE: 'REVENUE',
    ADVANCE_INVOICE: 'REVENUE',
    PURCHASE_INVOICE: 'COST',
    PAYROLL: 'COST',
    WORK_REPORT_LABOR: 'COST',
    PERSONNEL_COST: 'COST',
    PERSONNEL_AGGREGATE: 'COST',
};
export function isRevenueBucket(bucket) {
    return COMMESSA_BUCKET_KIND[bucket] === 'REVENUE';
}
function bucketsOfKind(kind) {
    return Object.keys(COMMESSA_BUCKET_KIND).filter((bucket) => COMMESSA_BUCKET_KIND[bucket] === kind);
}
/** Bucket che alimentano il VENDUTO, derivati dalla mappa. */
export const REVENUE_BUCKETS = bucketsOfKind('REVENUE');
/** Bucket che alimentano i COSTI, derivati dalla mappa. */
export const COST_BUCKETS = bucketsOfKind('COST');
//# sourceMappingURL=commessa-allocations.js.map